import React, {useCallback, useEffect, useRef} from "react";
import {useHistory} from "react-router-dom";
import {toast} from "react-toastify";
import {ToastContent} from "components";
import {useAppDispatch, useAppSelector} from "hooks/index";
import {TUrlImportItem, TProfilesImportInProgress} from "mapx-components/BulkImport/types";
import {CalculateStatusTypeCounts} from "mapx-components/Modals/BulkImportModal/Report/utils";
import ProjectTabs from "mapx-pages/Project/ProjectTabs";
import {clearAPWorkflowState} from "store/additional-profiles/additionalProfilesActions";
import {setImportLinkedInProfileInfoForCurrentImport} from "store/background-tasks/backgroundTaskActions";
import {getAllBackgroundBulkImports} from "store/background-tasks/backgroundTaskAsyncActions";
import {profilesImportsByLinkedInSelector} from "store/background-tasks/backgroundTaskSelectors";
import {
	removeBulkImportDataForSource,
	removeBulkRefreshDataForSource,
	resetBulkImportStatesForProject,
	setImportLinkedInProfileInfoForCompany,
	setLastCompleteImportLinkedInProfileInfoForProject,
	setShouldOpenBulkImportReport,
	setShowLastImportReport,
} from "store/bulk-import/bulkImportActions";
import {
	activeImportInfoForCompanySelector,
	lastCompletedCandidateImportByLinkedInForProjectSelector,
} from "store/bulk-import/bulkImportSelectors";
import {getIndividualBulkImportStatuses} from "store/bulk-import/bulkImportAsyncActions";
import {companySelector} from "store/company/companySelectors";
import {setProjectTabIndex} from "store/project-list/projectListActions";
import {
	getProjectNameByProjectIDSelector,
	projectSelector,
} from "store/project-list/projectListSelectors";
import {getPaginatedProjectSavedPeople} from "store/project/projectCandidatesAsyncActions";
import {updateProjectSuccessChecklistItem} from "store/project/projectSuccessChecklistAsyncActions";
import {getTargetListCompanies} from "store/target-list/targetListAsyncActions";
import {
	fetchTargetListCandidateIDs,
	getTargetListCandidates,
} from "store/target-list/targetListCandidatesAsyncActions";
import {TLinkedinBulkImportIndividualStatusResponse} from "../types";

/* eslint-disable  @typescript-eslint/no-explicit-any */

// Tracks the last known success count per background task ID
const lastKnownSuccessCount: Record<number, number> = {};

/**
 * Hook to monitor the progress of bulk LinkedIn profile imports.
 * Handles tracking, updating state, and showing live notifications for completed imports.
 */
const useBulkImportLinkedInProfileProgressWatcher = () => {
	const history = useHistory();

	const state = useAppSelector((state) => state);

	const currentProject = useAppSelector(projectSelector);

	const currentCompany = useAppSelector(companySelector);

	const activeImportInfoForCompany = useAppSelector(activeImportInfoForCompanySelector);

	const lastCompletedCandidateImportByLinkedInForProject = useAppSelector(
		lastCompletedCandidateImportByLinkedInForProjectSelector,
	);

	const importLinkedInProfileForProjects = useAppSelector(profilesImportsByLinkedInSelector);

	const dispatch = useAppDispatch();

	// Use Ref to persist intervals across renders without restarting them
	const intervalRefs = useRef<Record<number, any>>({});

	// Keep latest data in a ref so the interval can access it without being a dependency
	const latestDataRef = useRef<TProfilesImportInProgress[]>([]);
	useEffect(() => {
		latestDataRef.current = importLinkedInProfileForProjects;
	}, [importLinkedInProfileForProjects]);

	/**
	 * Navigates to the project or company page and opens import report modal.
	 */
	const handleSeeProjectClick = useCallback(
		async (projectID: number) => {
			if (!projectID) {
				history.push(`/company/${activeImportInfoForCompany?.id}`);
			} else {
				if (
					currentProject === null ||
					currentProject?.id.toString() !== projectID.toString()
				) {
					dispatch(clearAPWorkflowState());
					dispatch(getTargetListCandidates());
					dispatch(getPaginatedProjectSavedPeople());
					dispatch(getTargetListCompanies());
				}

				dispatch(setProjectTabIndex(ProjectTabs.savedPeopleTabIndex));
				history.push(`/project/${projectID}`);
			}
			dispatch(setShouldOpenBulkImportReport({id: projectID ?? 0, value: true}));

			toast.dismiss(`import_success_toaster${projectID ?? 0}`);
			toast.dismiss(`import_error_toaster${projectID ?? 0}`);
		},
		[dispatch, currentProject, history, activeImportInfoForCompany?.id],
	);

	const isImportInProgress = (status: string) => {
		return status === "Created" || status === "In Progress" || status === "Scraped";
	};

	/**
	 * Updates the import information in the Redux store.
	 */
	const updateImportInfo = useCallback(
		async (response: TLinkedinBulkImportIndividualStatusResponse, purpose?: string) => {
			dispatch(
				setImportLinkedInProfileInfoForCurrentImport({
					...response.data,
					purpose: purpose || "import",
					import_type: "Candidates Bulk Import",
				} as TProfilesImportInProgress),
			);

			const hasAddedPeople = response.data?.linkedin_candidate_urls?.some(
				(item: TUrlImportItem) =>
					["success", "imported", "completed", "already exists"].includes(
						item.status?.toLowerCase(),
					),
			);

			if (hasAddedPeople && response.data.project_id) {
				dispatch(
					updateProjectSuccessChecklistItem({
						attribute: "has_saved_people",
						value: true,
					}),
				);
			}
		},
		[dispatch],
	);

	const handleSuccessToast = useCallback(
		async (projectID: number, projectName: Nullable<string>) => {
			toast.success(ToastContent, {
				toastId: `import_success_toaster${projectID}`,
				autoClose: false,
				closeOnClick: false,
				data: {
					title: `Import is completed for ${projectName}`,
					description: () => (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "2px",
								fontSize: "12px",
							}}
						>
							<p style={{width: "190px"}}>
								An import has successfully completed without errors, click on report
								to see imported profiles.
							</p>
							<button
								style={{
									backgroundColor: "#309161",
									color: "#ECF9F2",
									borderRadius: "5px",
									marginTop: "-20px",
									width: "60px",
									cursor: "pointer",
								}}
								onClick={() => handleSeeProjectClick(projectID)}
							>
								Report
							</button>
						</div>
					),
				},
				delay: 100,
			});
		},
		[handleSeeProjectClick],
	);

	const handleErrorToast = useCallback(
		async (projectID: number, projectName: Nullable<string>) => {
			toast.warning(ToastContent, {
				toastId: `import_error_toaster${projectID}`,
				autoClose: false,
				closeOnClick: false,
				data: {
					title: `Import is completed with errors for ${projectName}`,
					description: () => (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "2px",
								fontSize: "12px",
							}}
						>
							<p style={{width: "190px"}}>
								An import has completed with errors, click on report to see imported
								profiles.
							</p>
							<button
								style={{
									backgroundColor: "#CC8800",
									color: "#ECF9F2",
									borderRadius: "5px",
									marginTop: "-20px",
									width: "60px",
									cursor: "pointer",
								}}
								onClick={() => handleSeeProjectClick(projectID)}
							>
								Report
							</button>
						</div>
					),
				},
				delay: 100,
			});
		},
		[handleSeeProjectClick],
	);

	const handlePartialCompletionToast = useCallback(
		async (
			projectID: number,
			projectName: Nullable<string>,
			completedCount: number,
			inProgressCount: number,
		) => {
			toast.info(ToastContent, {
				toastId: `import_partial_toaster${projectID}`,
				autoClose: false,
				closeOnClick: false,
				data: {
					title: `Import in progress for ${projectName}`,
					description: () => (
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: "2px",
								fontSize: "12px",
							}}
						>
							<p style={{width: "190px"}}>
								Import has completed for {completedCount} profile
								{completedCount !== 1 ? "s" : ""}. {inProgressCount} profile,
								{inProgressCount !== 1 ? "s are" : " is"} still being imported.
								Click here to view.
							</p>
							<button
								style={{
									backgroundColor: "#4A90E2",
									color: "#ECF9F2",
									borderRadius: "5px",
									marginTop: "-20px",
									width: "60px",
									cursor: "pointer",
								}}
								onClick={() => handleSeeProjectClick(projectID)}
							>
								View
							</button>
						</div>
					),
				},
				delay: 100,
			});
		},
		[handleSeeProjectClick],
	);

	/**
	 * Updates the redux state with latest completed bulk import data
	 */
	const updateImportHistoryOnState = useCallback(
		(response: TLinkedinBulkImportIndividualStatusResponse, urls: TUrlImportItem[]) => {
			const projectID = response.data.project_id ?? 0;
			const previouslyImportedData =
				lastCompletedCandidateImportByLinkedInForProject[projectID]?.importInfo
					?.linkedin_candidate_urls || [];

			const mergedData = {
				id: projectID,
				status: "Completed",
				importInfo: {
					linkedin_candidate_urls: [...urls],
				},
			};

			previouslyImportedData.forEach((item: TUrlImportItem) => {
				mergedData.importInfo.linkedin_candidate_urls.push(item);
			});

			const payload = {id: projectID, data: mergedData};

			dispatch(setLastCompleteImportLinkedInProfileInfoForProject(payload));
		},
		[lastCompletedCandidateImportByLinkedInForProject, dispatch],
	);

	const getImportSource = useCallback(
		(projectID: Nullable<number>): string => {
			if (projectID) {
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				return getProjectNameByProjectIDSelector(state, projectID);
			} else {
				const currentImportCompanyInfo = activeImportInfoForCompanySelector(state);

				return currentImportCompanyInfo?.name ?? "Unknown";
			}
		},
		[state],
	);

	/**
	 * Handles post-completion actions for candidate imports
	 */
	const handlePostCompletionActions = useCallback(
		async (
			response: TLinkedinBulkImportIndividualStatusResponse,
			errorImportingCount: number,
			purpose?: string,
		) => {
			const projectId = response.data.project_id ?? 0;

			dispatch(getTargetListCandidates());
			dispatch(fetchTargetListCandidateIDs());

			if (response.data.project_id === currentProject?.id) {
				dispatch(getPaginatedProjectSavedPeople());
			}

			toast.dismiss(`import_started_toaster${projectId}`);

			const importSource = getImportSource(response.data.project_id);

			if (purpose === "import") {
				const urls = response.data.linkedin_candidate_urls;
				const statusCounts = CalculateStatusTypeCounts(urls);

				const toastMessage = `${
					response.data.project_id ? "Project: " : "Company: "
				}${importSource}`;

				if (statusCounts.inProgress > 0) {
					const completedCount = statusCounts.success + statusCounts.error;
					await handlePartialCompletionToast(
						projectId,
						toastMessage,
						completedCount,
						statusCounts.inProgress,
					);
				} else if (errorImportingCount === 0) {
					await handleSuccessToast(projectId, toastMessage);
				} else {
					await handleErrorToast(projectId, toastMessage);
				}
			}
		},
		[
			currentProject?.id,
			handleSuccessToast,
			handleErrorToast,
			handlePartialCompletionToast,
			dispatch,
			getImportSource,
		],
	);

	const handleCompletedImport = useCallback(
		async (response: TLinkedinBulkImportIndividualStatusResponse, purpose?: string) => {
			const urls = response.data.linkedin_candidate_urls;
			updateImportHistoryOnState(response, urls);

			if (urls && urls.length > 0) {
				const errorImportingCount: number = urls.filter((item: TUrlImportItem) =>
					["bad url", "not found", "scraping error", "import error"].includes(
						item.status?.toLowerCase(),
					),
				).length;

				const stillInProgressImport: number = urls.filter((item: TUrlImportItem) =>
					["created", "in progress", "scraped"].includes(item.status?.toLowerCase()),
				).length;

				if (response.data.progress === 100 && stillInProgressImport === 0) {
					if (
						!response.data.project_id &&
						activeImportInfoForCompany?.id === currentCompany?.id
					) {
						const payload = {
							data: response.data.linkedin_candidate_urls,
							id: activeImportInfoForCompany?.id,
						};
						dispatch(setImportLinkedInProfileInfoForCompany(payload));
					}

					if (purpose === "refresh") {
						const removeFrmStatePayload = {
							project_id: response.data.project_id,
							urls: response.data.linkedin_candidate_urls,
						};
						dispatch(removeBulkRefreshDataForSource(removeFrmStatePayload));
					}
				}

				if (purpose === "import") {
					const projectId = response.data.project_id ?? 0;
					dispatch(removeBulkImportDataForSource(projectId));
					dispatch(resetBulkImportStatesForProject(projectId));
					dispatch(setShowLastImportReport(true));
					dispatch(getAllBackgroundBulkImports());
				}

				await handlePostCompletionActions(response, errorImportingCount, purpose);
			}

			const taskId = response.data?.id;
			if (intervalRefs.current[taskId]) {
				clearInterval(intervalRefs.current[taskId]);
				delete intervalRefs.current[taskId];
			}
		},
		[
			updateImportHistoryOnState,
			activeImportInfoForCompany?.id,
			currentCompany?.id,
			dispatch,
			handlePostCompletionActions,
		],
	);

	/**
	 * Polling for candidate bulk imports
	 */
	useEffect(() => {
		const inProgressLinkedinBulkImports =
			importLinkedInProfileForProjects?.filter((imp: any) => {
				const typeMatch =
					imp.type === "Candidates Bulk Import" ||
					imp.import_type === "Candidates Bulk Import";
				const fallbackMatch = !imp.type && !imp.import_type; // Backwards compat

				return typeMatch || fallbackMatch;
			}) || [];

		inProgressLinkedinBulkImports.forEach((data) => {
			if (data && isImportInProgress(data.status) && data.id) {
				const taskId = data.id;

				if (!intervalRefs.current[taskId]) {
					intervalRefs.current[taskId] = setInterval(async () => {
						try {
							const response = (await dispatch(
								getIndividualBulkImportStatuses(taskId) as any,
							)) as TLinkedinBulkImportIndividualStatusResponse;

							if (!response || "error" in response) {
								return;
							}

							const latestData = latestDataRef.current.find((d) => d.id === taskId);
							const purpose = latestData?.purpose || data.purpose;

							await updateImportInfo(response, purpose);

							const urls = response.data?.linkedin_candidate_urls || [];
							const currentSuccessCount = urls.filter((item: TUrlImportItem) =>
								["success", "imported", "completed", "already exists"].includes(
									item.status?.toLowerCase(),
								),
							).length;

							const prevSuccessCount = lastKnownSuccessCount[taskId] ?? 0;

							if (currentSuccessCount > prevSuccessCount) {
								lastKnownSuccessCount[taskId] = currentSuccessCount;
								dispatch(getTargetListCandidates());
								dispatch(fetchTargetListCandidateIDs());

								if (response.data?.project_id === currentProject?.id) {
									dispatch(getPaginatedProjectSavedPeople());
								}
							}

							if (response.status === "Completed") {
								delete lastKnownSuccessCount[taskId];
								if (intervalRefs.current[taskId]) {
									clearInterval(intervalRefs.current[taskId]);
									delete intervalRefs.current[taskId];
								}
								await handleCompletedImport(response, purpose);
							}
						} catch (error) {
							console.error("Error fetching bulk import status:", error);
						}
					}, 8 * 1000);
				}
			}
		});

		// Cleanup: identify tasks that are no longer in progress and stop their intervals
		const inProgressIds = new Set(inProgressLinkedinBulkImports.map((d) => d.id));
		Object.keys(intervalRefs.current).forEach((idStr) => {
			const id = parseInt(idStr);
			if (!inProgressIds.has(id)) {
				clearInterval(intervalRefs.current[id]);
				delete intervalRefs.current[id];
			}
		});
	}, [
		importLinkedInProfileForProjects,
		dispatch,
		handleCompletedImport,
		updateImportInfo,
		currentProject?.id,
	]);

	// Final cleanup on unmount
	useEffect(() => {
		return () => {
			Object.values(intervalRefs.current).forEach(clearInterval);
			intervalRefs.current = {};
		};
	}, []);

	return null;
};

export default useBulkImportLinkedInProfileProgressWatcher;
